import { OrganizerEventResponse, OrganizerEvent } from './types';
import type { EventInteraction, EventInteractionWithContext } from '@/tracking/types';
import { getUrgencySignalsForTracking } from '@/components/UrgencySignals/utils';

const NUMBER_OF_MORE_EVENTS_TO_SHOW = 5;

// Get events to show (limit to 4)
export const getEventsToShow = (
  organizerEvents: OrganizerEventResponse | null,
  currentEventId: string,
): OrganizerEvent[] => {
  if (!organizerEvents?.events?.length) {
    return [];
  }
  // Filter out the current event and limit to 4
  return organizerEvents.events.filter((event) => event.id !== currentEventId).slice(0, 4);
};

// Create tracking information getter
export const createTrackingInformationGetter = (
  mainEvent: { id?: string; isOnline?: boolean; isEnded?: boolean },
  allEvents: OrganizerEvent[] = [],
  locale: string,
  sessionInfo: {
    userId?: string | null;
    sessionId?: string | null;
    guestId?: string | null;
  },
) => {
  return (eventInteraction: EventInteraction): EventInteractionWithContext => {
    // Find the specific event from the events array for rank calculation
    const eventToTrack = allEvents.find((e) => e.id === eventInteraction.id);

    if (!eventToTrack) {
      console.error(`Event with id ${eventInteraction.id} not found in organizer events list`);
      return {} as EventInteractionWithContext;
    }

    const rank = allEvents.findIndex((e) => e.id === eventInteraction.id) + 1;
    const hasOpenPromotion = eventToTrack.hasOpenDiscount ?? false;
    const hasBOGOLabel = eventToTrack.specialDiscounts?.hasBogoTickets ?? false;
    const urgencySignals = getUrgencySignalsForTracking(eventToTrack.urgencySignals);

    return {
      id: eventInteraction.id,
      action: eventInteraction.action,
      event: eventInteraction.event,
      user: {
        id: sessionInfo.userId ?? undefined,
        locale,
        guestId: sessionInfo.guestId ?? undefined,
        sessionId: sessionInfo.sessionId ?? undefined,
      },
      place: {
        name: 'more_organizer_events',
        position: rank,
        page: 1,
        context: {
          main_event_id: mainEvent.id,
          is_online: mainEvent.isOnline,
          is_ended: mainEvent.isEnded,
          total_organizer_events: allEvents.length,
        },
      },
      displayContext: {
        hasOpenPromotion,
        hasBOGOLabel,
        urgencySignals,
      },
      venueId: eventToTrack.venue?.id,
    };
  };
};

export const getMoreEventsFromThisOrganizerFiltered = ({
  currentEventId,
  events,
}: {
  currentEventId: string;
  events: OrganizerEvent[];
}): OrganizerEvent[] => {
  return (
    events
      ?.filter(
        (event: OrganizerEvent) => event.ticket_availability?.has_available_tickets && event.id !== currentEventId,
      )
      .slice(0, NUMBER_OF_MORE_EVENTS_TO_SHOW) || []
  );
};
